import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ClassDetailsModal = ({ 
  classData, 
  onClose,
  onBook,
  isBooked 
}) => {
  if (!classData) return null;

  const {
    name,
    instructor,
    instructorImage,
    instructorImageAlt,
    instructorBio,
    time,
    duration,
    difficulty,
    spotsLeft,
    totalSpots,
    category,
    categoryColor,
    description,
    equipment,
    benefits,
    reviews
  } = classData;

  const getDifficultyColor = (level) => {
    const colors = {
      'Beginner': 'text-success bg-success/10',
      'Intermediate': 'text-warning bg-warning/10',
      'Advanced': 'text-error bg-error/10'
    };
    return colors?.[level] || colors?.['Beginner'];
  };

  const isFull = spotsLeft === 0;
  const averageRating = reviews?.reduce((acc, r) => acc + r?.rating, 0) / reviews?.length;

  return (
    <div 
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto overflow-elegant"
        onClick={(e) => e?.stopPropagation()}
      >
        <div 
          className="h-3"
          style={{ backgroundColor: categoryColor }}
          aria-hidden="true"
        />

        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                {name}
              </h2>
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Icon name="Clock" size={16} />
                  <span>{time}</span>
                </div>
                <span className="text-border">•</span>
                <span>{duration} minutes</span>
                <span className="text-border">•</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(difficulty)}`}>
                  {difficulty}
                </span>
              </div>
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              iconName="X"
              iconSize={20}
              aria-label="Close modal"
            />
          </div>

          <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg mb-6">
            <div className="relative w-16 h-16 md:w-20 md:h-20 flex-shrink-0">
              <Image
                src={instructorImage}
                alt={instructorImageAlt}
                className="w-full h-full rounded-full object-cover"
              />
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-success rounded-full border-2 border-card" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-heading font-semibold text-foreground mb-1">
                {instructor}
              </h3>
              <p className="text-sm text-muted-foreground mb-2">
                {category} Instructor
              </p>
              <p className="text-sm text-foreground line-clamp-2">
                {instructorBio}
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="FileText" size={20} color="var(--color-primary)" />
                Description
              </h3>
              <p className="text-sm md:text-base text-foreground leading-relaxed">
                {description}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Dumbbell" size={20} color="var(--color-primary)" />
                Equipment Needed
              </h3>
              <div className="flex flex-wrap gap-2">
                {equipment?.map((item, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium caption"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Target" size={20} color="var(--color-primary)" />
                Benefits
              </h3>
              <ul className="space-y-2">
                {benefits?.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm md:text-base text-foreground">
                    <Icon name="Check" size={18} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-heading font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Star" size={20} color="var(--color-primary)" />
                  Member Reviews
                </h3>
                <div className="flex items-center gap-1">
                  <Icon name="Star" size={16} color="var(--color-warning)" fill="var(--color-warning)" />
                  <span className="text-sm font-medium data-text text-foreground">
                    {averageRating?.toFixed(1)}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    ({reviews?.length})
                  </span>
                </div>
              </div>
              <div className="space-y-3 max-h-48 overflow-y-auto overflow-elegant">
                {reviews?.slice(0, 3)?.map((review) => (
                  <div key={review?.id} className="p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-foreground">
                        {review?.memberName}
                      </span>
                      <div className="flex items-center gap-1">
                        {[...Array(5)]?.map((_, i) => (
                          <Icon
                            key={i}
                            name="Star"
                            size={12}
                            color={i < review?.rating ? 'var(--color-warning)' : 'var(--color-muted)'}
                            fill={i < review?.rating ? 'var(--color-warning)' : 'none'}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {review?.comment}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon 
                  name="Users" 
                  size={20} 
                  color={isFull ? 'var(--color-error)' : 'var(--color-foreground)'} 
                />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {spotsLeft} spots remaining
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {totalSpots} total capacity
                  </p>
                </div>
              </div>
              
              {!isBooked && (
                <Button
                  variant={isFull ? "secondary" : "default"}
                  size="lg"
                  onClick={() => {
                    onBook(classData?.id);
                    onClose();
                  }}
                  disabled={isFull}
                  iconName={isFull ? "Clock" : "Calendar"}
                  iconPosition="left"
                  iconSize={20}
                >
                  {isFull ? 'Join Waitlist' : 'Book This Class'}
                </Button>
              )}
              
              {isBooked && (
                <div className="flex items-center gap-2 text-success">
                  <Icon name="CheckCircle2" size={20} />
                  <span className="text-sm font-medium">Already Booked</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassDetailsModal;